import { Annotation, isAnnotationNote, isPointNote } from "./annotationsHelper";

const ICON_COLLISION_TOLERANCE = 8;
const ICON_COLLISION_GAP = 4;
const ICON_FALLBACK_WIDTH = 21;

/** Absolute position, plus an optional explicit size, for a rendered icon. */
interface IconStyle {
  top: number;
  left: number;
  size?: number;
}

/** A single rendered view inside the epub.js rendition. */
interface RenditionView {
  element: HTMLElement;
  layout: { props: { pageWidth: number; width: number } };
}

interface Rendition {
  getRange(location: string): Range | null;
  views(): { _views: RenditionView[] };
}

/** The subset of ViewerSettings the renderer reads. */
interface ViewerSettings {
  flow: string;
}

/**
 * The parts of the EpubViewer component the renderer depends on. EpubViewer is
 * still plain JavaScript, so this captures only what AnnotationRenderer uses.
 */
interface EpubViewerHost {
  rendition: Rendition;
  openNoteEditor(note: Annotation): void;
  props: {
    viewerSettings: ViewerSettings;
    annotationsEnabled?: boolean;
    onAnnotationClicked?: (item: Annotation) => void;
  };
}

/** location → DOM element map for drawn icons. */
type IconMap = Record<string, Element>;

export default class AnnotationRenderer {
  private epubViewer: EpubViewerHost;
  private bookmarkIcons: IconMap;
  private noteIcons: IconMap;
  private highlightNoteIcons: IconMap;
  private parser: DOMParser;
  private iconColor: string;

  /**
   * Renders bookmark and note icons inside the epub viewer.
   * @param epubViewer Reference to the epubViewer that the annotation icons will be added to.
   */
  constructor(epubViewer: EpubViewerHost) {
    this.epubViewer = epubViewer;
    this.bookmarkIcons = {}; // bookmark icons: location → DOM element
    this.noteIcons = {}; // note icons: location → DOM element
    this.highlightNoteIcons = {}; // end-of-highlight pencil icons: location → DOM element
    this.parser = new DOMParser();
  }

  /**
   * Reconcile every annotation icon in one call: bookmark and point-note margin
   * icons, plus the pencil markers at the end of highlights that carry note
   * text. Bookmarks are drawn before point notes so colliding icons land in a
   * fixed order. The full note list can be passed; point-note and highlight-note
   * icons are each filtered from it internally.
   * @param bookmarks Annotations with AnnotationType.Bookmark
   * @param notes Notes and highlights; highlights without note text are ignored
   */
  renderAnnotations(bookmarks: Annotation[], notes: Annotation[]): void {
    this.drawBookmarks(bookmarks);
    this.drawNotes(notes);
    this.drawHighlightNoteIcons(notes);
    this.layoutMarginIcons();
  }

  /**
   * Erase rendered icons
   */
  clearAnnotations() {
    this.clearIcons(this.bookmarkIcons);
    this.clearIcons(this.noteIcons);
    this.clearIcons(this.highlightNoteIcons);
  }

  /**
   * Clear every annotation icon and draw them all again from scratch.
   * @param bookmarks Annotations with AnnotationType.Bookmark
   * @param notes Notes and highlights; highlights without note text are ignored
   */
  clearAndRerenderAnnotations(
    bookmarks: Annotation[],
    notes: Annotation[],
  ): void {
    this.clearAnnotations();
    this.renderAnnotations(bookmarks, notes);
  }

  // ─── Bookmarks ────────────────────────────────────────────────────────────

  /**
   * Draws all bookmarks provided to the renderer
   * @param bookmarks List of annotations with AnnotationType.Bookmark
   */
  drawBookmarks(bookmarks: Annotation[]): void {
    this.drawIcons(bookmarks, this.bookmarkIcons, (bookmark) =>
      this.drawBookmark(bookmark),
    );
  }

  /**
   * Draw a single bookmark icon.
   * @param bookmark Annotation with a .location to mark
   */
  drawBookmark(bookmark: Annotation): Element | null {
    const svg = `<svg data-prefix="fas" data-icon="bookmark" class="svg-inline--fa fa-bookmark fa-w-12 fa-lg" role="presentation" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path fill="${this.iconColor}" d="M0 512V48C0 21.49 21.49 0 48 0h288c26.51 0 48 21.49 48 48v464L192 400 0 512z"></path></svg>`;
    return this.drawIcon(bookmark, svg);
  }

  // ─── Notes ────────────────────────────────────────────────────────────────

  /**
   * Draws all point notes provided to the renderer
   * @param notes List of annotations with AnnotationType.Note
   */
  drawNotes(notes: Annotation[]): void {
    const pointNotes = notes.filter((n) => isPointNote(n));
    this.drawIcons(pointNotes, this.noteIcons, (note) => this.drawNote(note));
  }

  /**
   * Draw a single pencil icon for a point note.
   * @param note Annotation with .location and .note
   * @returns Element representing the given point note
   */
  drawNote(note: Annotation): Element | null {
    return this.drawIcon(note, this.notePencilSvg(), (clicked) =>
      this.epubViewer.openNoteEditor(clicked),
    );
  }

  /** Font Awesome pencil icon, filled with the accent purple. */
  notePencilSvg(): string {
    return `<svg data-prefix="fas" data-icon="pencil" class="svg-inline--fa fa-pencil fa-w-16 fa-lg" role="presentation" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="${this.iconColor}" d="M497.9 74.16L437.8 14.06c-18.75-18.75-49.19-18.75-67.93 0l-56.53 56.55 127.1 128 56.56-56.55C516.7 123.3 516.7 92.91 497.9 74.16zM290.8 93.23l-259.7 259.7c-2.234 2.234-3.755 5.078-4.376 8.176l-26.34 131.7C-1.921 504 7.95 513.9 19.15 511.6l131.7-26.34c3.098-.6191 5.941-2.139 8.175-4.373l259.7-259.7L290.8 93.23z"/></svg>`;
  }

  // ─── Highlight note markers ─────────────────────────────────────────────────

  /**
   * Draws a small pencil marker at the end of every highlight that carries note
   * text. Point notes are excluded — they get their own margin icon via drawNotes.
   * @param notes List of annotations; only span notes with text are marked.
   */
  drawHighlightNoteIcons(notes: Annotation[]): void {
    const highlightNotes = notes.filter(
      (note) => isAnnotationNote(note) && !isPointNote(note),
    );
    this.drawIcons(highlightNotes, this.highlightNoteIcons, (note) =>
      this.drawHighlightNoteIcon(note),
    );
  }

  /**
   * Draw a single pencil marker at the end of a highlight that has note text.
   * @param note Annotation with a span .location and .note
   * @returns Element representing the marker
   */
  drawHighlightNoteIcon(note: Annotation): Element | null {
    return this.drawIcon(
      note,
      this.notePencilSvg(),
      (clickedNote) => this.epubViewer.openNoteEditor(clickedNote),
      (range) => this.computeHighlightEndIconStyle(range),
    );
  }

  // ─── Shared helpers ───────────────────────────────────────────────────────

  /**
   * Draw a single icon for an annotation: position it next to the annotation's
   * range, wire up the click handler, and append it to the view.
   * @param item Annotation with a .location and .type
   * @param svg SVG markup for the icon
   * @param onClick Handles a click on the icon; defaults to notifying
   *   onAnnotationClicked (used by bookmarks). Point notes and highlight-note
   *   markers override it to open the note editor.
   * @param computeStyle Positions the icon relative to its range; defaults to
   *   the margin placement used by bookmarks and point notes
   * @returns The appended icon element, or null on failure
   */
  drawIcon(
    item: Annotation,
    svg: string,
    onClick: (item: Annotation) => void = (clicked) =>
      this.epubViewer.props.onAnnotationClicked?.(clicked),
    computeStyle: (range: Range) => IconStyle = (range) =>
      this.computeMarginIconStyle(range),
  ): Element | null {
    const range = this.epubViewer.rendition.getRange(item.location);
    if (range) {
      try {
        const view = this.currentView();
        const style = computeStyle(range);
        const el = this.createIconElement(style, svg);

        if (this.epubViewer.props.annotationsEnabled) {
          el.style.cursor = "pointer";
          el.addEventListener("click", (e) => {
            e.stopPropagation();
            onClick(item);
          });
        }

        return view.element.appendChild(el);
      } catch (error) {
        console.log(`Error while trying to draw ${item.type} icon.`);
        console.log(error);
      }
    }
    return null;
  }

  /**
   * Give bookmarks and point notes a fixed order in the margin: a bookmark
   * keeps the margin-edge spot and a note on the same line sits to its right,
   * regardless of which was drawn first. Notes without a bookmark on their line
   * stay on the margin edge.
   */
  layoutMarginIcons(): void {
    for (let noteLocation in this.noteIcons) {
      const noteElement = this.noteIcons[noteLocation] as HTMLElement;
      const bookmarkElement = this.collidingBookmark(noteElement);
      if (bookmarkElement) {
        const bookmarkWidth =
          bookmarkElement.getBoundingClientRect().width || ICON_FALLBACK_WIDTH;
        const left =
          parseFloat(bookmarkElement.dataset.baseLeft!) +
          bookmarkWidth +
          ICON_COLLISION_GAP;
        noteElement.style.left = `${left}px`;
      } else {
        noteElement.style.left = `${noteElement.dataset.baseLeft}px`;
      }
    }
  }

  /**
   * Find a bookmark icon sharing the given note icon's line and margin column,
   * or null if none. Compared against each icon's un-shifted base position.
   */
  collidingBookmark(noteElement: HTMLElement): HTMLElement | null {
    const noteTop = parseFloat(noteElement.style.top);
    const noteBaseLeft = parseFloat(noteElement.dataset.baseLeft!);
    for (let location in this.bookmarkIcons) {
      const bookmarkElement = this.bookmarkIcons[location] as HTMLElement;
      if (
        Math.abs(parseFloat(bookmarkElement.style.top) - noteTop) <
          ICON_COLLISION_TOLERANCE &&
        Math.abs(parseFloat(bookmarkElement.dataset.baseLeft!) - noteBaseLeft) <
          ICON_COLLISION_TOLERANCE
      ) {
        return bookmarkElement;
      }
    }
    return null;
  }

  /**
   * Sync the drawn icons for a set of annotations against the provided icon
   * map: remove icons whose location is no longer present, and draw any that
   * are missing.
   * @param items Annotations to draw, each with a .location
   * @param iconMap location → DOM element map to reconcile
   * @param drawIcon Draws a single icon, returns its element
   */
  drawIcons(
    items: Annotation[],
    iconMap: IconMap,
    drawIcon: (item: Annotation) => Element | null,
  ): void {
    this.iconColor = "#972585";
    const locations = items.map((item) => item.location);

    for (let location in iconMap) {
      if (locations.indexOf(location) === -1) {
        this.removeIcon(iconMap, location);
      }
    }

    for (let item of items) {
      if (!iconMap[item.location]) {
        const el = drawIcon(item);
        if (el) {
          iconMap[item.location] = el;
        }
      }
    }
  }

  /**
   * Remove every icon in the provided map from the DOM.
   * @param iconMap location → DOM element map to clear
   */
  clearIcons(iconMap: IconMap): void {
    for (let location in iconMap) {
      this.removeIcon(iconMap, location);
    }
  }

  /**
   * Remove a single icon from the DOM and the provided map.
   * @param iconMap location → DOM element map
   * @param location Location whose icon should be removed
   */
  removeIcon(iconMap: IconMap, location: string): void {
    const el = iconMap[location];
    el?.parentElement?.removeChild(el);
    delete iconMap[location];
  }

  /**
   * Compute absolute position for a margin icon given a range.
   * @param range Range the annotation is associated with.
   * @returns IconStyle - top and left absolute positioning for the icon.
   */
  computeMarginIconStyle(range: Range): IconStyle {
    const view = this.currentView();
    const layout = view.layout;
    const vs = this.epubViewer.props.viewerSettings;

    // A collapsed (point) range has no client rects, so it can't be measured
    // directly. Expand a clone by one position to get a measurable rectangle
    // without mutating the range the caller still holds.
    const measureRange = range.cloneRange();
    const { endContainer, endOffset } = measureRange;
    if (
      endContainer.nodeType === Node.TEXT_NODE &&
      endOffset < (endContainer as Text).length
    ) {
      measureRange.setEnd(endContainer, endOffset + 1);
    } else {
      measureRange.setEndAfter(endContainer);
    }
    const rect = measureRange.getClientRects()[0]!;

    const style: IconStyle = { top: rect.top, left: 0 };

    if (vs.flow === "paginated") {
      style.left =
        Math.floor(rect.left / layout.props.pageWidth) *
          layout.props.pageWidth +
        layout.props.width / 48;
    } else {
      style.left = layout.props.width / 24;
    }

    style.left -= 8;

    return style;
  }

  /**
   * Compute the position and size for an icon sitting in the whitespace at the end
   * of a highlight. The marker is small — about the width of the trailing space — and
   * sits flush with the top of the highlight's last line box so it floats like a
   * superscript next to the final word instead of overlapping the following text.
   * @param range Range of the highlight the icon belongs to.
   * @returns IconStyle positioned at the end of the highlight.
   */
  computeHighlightEndIconStyle(range: Range): IconStyle {
    const rects = range.getClientRects();
    const lastRect = rects[rects.length - 1];
    if (!lastRect) return this.computeMarginIconStyle(range);

    const lineHeight = lastRect.height || 16;
    const size = Math.round(lineHeight * 0.4);
    const leftNudge = Math.round(size / 4);
    return {
      top: lastRect.top,
      left: lastRect.right - leftNudge,
      size,
    };
  }

  /**
   * Creates a div containing an SVG that represents the annotation
   * @param style Top and Left positioning for the icon (and optional size)
   * @param svgMarkup Font Awesome icon to use for the icon
   * @returns HTMLDivElement containing the SVG with the applied positioning
   */
  createIconElement(style: IconStyle, svgMarkup: string): HTMLDivElement {
    const view = this.currentView();
    const el = view.element.ownerDocument.createElement("div");
    el.style.position = "absolute";
    el.style.top = `${style.top}px`;
    el.style.left = `${style.left}px`;
    const svg = this.parser.parseFromString(
      svgMarkup,
      "image/svg+xml",
    ).documentElement;
    if (style.size != null) {
      el.style.width = `${style.size}px`;
      el.style.height = `${style.size}px`;
      svg.style.display = "block";
      svg.style.width = "100%";
      svg.style.height = "100%";
    }
    el.dataset.baseLeft = `${style.left}`;
    el.appendChild(svg);
    return el;
  }

  /**
   * Returns the first view in the rendition views collection.
   */
  currentView(): RenditionView {
    return this.epubViewer.rendition.views()._views[0]!;
  }
}
